/**
 * WebGL Shader definitions and compilation utilities
 */

export const vertexShaderSource = `
  attribute vec3 aPosition;
  attribute vec2 aTexCoord;
  attribute vec3 aNormal;

  uniform mat4 uProjection;
  uniform mat4 uModelView;

  varying vec2 vTexCoord;
  varying float vFacing;
  varying float vIsSide;

  void main() {
    gl_Position = uProjection * uModelView * vec4(aPosition, 1.0);
    vTexCoord = aTexCoord;

    // Transform normal to view space
    vec4 transformedNormal = uModelView * vec4(aNormal, 0.0);
    vFacing = transformedNormal.z > 0.0 ? 1.0 : 0.0;

    // Check if this is a side face (normal has no Z component in model space)
    vIsSide = abs(aNormal.z) < 0.01 ? 1.0 : 0.0;
  }
`;

export const fragmentShaderSource = `
  precision mediump float;

  uniform sampler2D uTexture;
  uniform float uOpacity;

  varying vec2 vTexCoord;
  varying float vFacing;
  varying float vIsSide;

  void main() {
    vec4 texColor = texture2D(uTexture, vTexCoord);

    // Discard fully transparent pixels
    if (texColor.a < 0.01) {
      discard;
    }

    if (vIsSide > 0.5) {
      // Side face - use the texture color to blend with edges
      gl_FragColor = vec4(texColor.rgb, texColor.a * 0.5);
    } else if (vFacing > 0.5) {
      // Front face - keep texture transparency intact
      gl_FragColor = vec4(texColor.rgb, texColor.a * uOpacity);
    } else {
      // Back face - show texture with reduced opacity
      gl_FragColor = vec4(texColor.rgb, texColor.a * 1.0);
    }
  }
`;

export function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compilation error:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }

  return shader;
}

export function createShaderProgram(gl) {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);

  const program = gl.createProgram();
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Shader program failed to link:', gl.getProgramInfoLog(program));
    return null;
  }

  return {
    program,
    attribLocations: {
      position: gl.getAttribLocation(program, 'aPosition'),
      texCoord: gl.getAttribLocation(program, 'aTexCoord'),
      normal: gl.getAttribLocation(program, 'aNormal')
    },
    uniformLocations: {
      projection: gl.getUniformLocation(program, 'uProjection'),
      modelView: gl.getUniformLocation(program, 'uModelView'),
      texture: gl.getUniformLocation(program, 'uTexture'),
      opacity: gl.getUniformLocation(program, 'uOpacity')
    }
  };
}
